import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useAuthContext } from '../components/AuthWrapper'; // Adjust path as needed
import { orgPropAPI } from '../utils/apiClient'; // Adjust path as needed

const CACHE_KEY = 'user_emails_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export const useUserEmailCache = () => {
  const auth = useAuth();
  const { orgHash } = useAuthContext();
  
  const [userCache, setUserCache] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only fetch if we have auth and org
    if (auth.user?.access_token && orgHash) {
      loadCache();
    } else {
      setIsLoading(false);
    }
  }, [auth.user?.access_token, orgHash]);

  const loadCache = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp, org } = JSON.parse(cached);
        const now = Date.now();
        
        // Check if cache is valid AND for the same org
        if (now - timestamp < CACHE_DURATION && org === orgHash) {
          console.log('Using cached user emails for org:', orgHash);
          setUserCache(data);
          setIsLoading(false);
          return;
        } else {
          console.log('Cache expired or org changed, removing...');
          localStorage.removeItem(CACHE_KEY);
        }
      }
      
      fetchUserEmails();
    } catch (error) {
      console.error('Error loading user cache:', error);
      fetchUserEmails();
    }
  };

  const fetchUserEmails = async () => {
    try {
      setIsLoading(true);
      
      const token = auth.user?.access_token;
      
      if (!token || !orgHash) {
        console.warn('Missing token or orgHash');
        setIsLoading(false);
        return;
      }
      
      console.log('Fetching user emails for org:', orgHash);
      
      // Use existing getOrgUsers endpoint
      const response = await orgPropAPI.getOrgUsers(token, orgHash);
      
      // Transform the response into UUID -> email mapping
      const userEmailMap = {};
      if (response.users && Array.isArray(response.users)) {
        response.users.forEach(user => {
          if (user.userID && user.email) {
            userEmailMap[user.userID] = user.email;
          }
        });
      }
      
      console.log(`Fetched ${Object.keys(userEmailMap).length} user emails`);
      
      setUserCache(userEmailMap);
      
      // Store with org hash to invalidate cache when org changes
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data: userEmailMap,
        timestamp: Date.now(),
        org: orgHash
      }));
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching user emails:', error);
      setIsLoading(false);
    }
  };

  const getUserEmail = (uuid) => {
    if (!uuid) return '-';
    return userCache[uuid] || uuid;
  };

  const clearCache = () => {
    console.log('Clearing user email cache');
    localStorage.removeItem(CACHE_KEY);
    setUserCache({});
    fetchUserEmails();
  };

  return { 
    getUserEmail, 
    isLoading,
    clearCache,
    cacheSize: Object.keys(userCache).length
  };
};